import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Form,
  Input,
  Select,
  Space,
  Typography,
  Steps,
  Switch,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  CommentContext,
  SubredditGuidelinesResponse,
} from '../../../services/aiReplyService';
import { NestedComment } from '../../../services/replyPostsService';
import { CommentTarget, GenerateParams } from './types';
import KeywordsInput from './KeywordsInput';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// Reddit-inspired neutral palette (mirrors ReplyComposerDrawer)
const T = {
  brand: '#FF4500',
  bg: '#FFFFFF',
  bgMuted: '#F8F9FA',
  border: '#E5E7EB',
  text: '#0F0F10',
  textSecondary: '#5C5C66',
  textMuted: '#898A8F',
  warning: '#B45309',
};

export interface QuickReplyFormHandle {
  submit: () => void;
}

interface QuickReplyFormProps {
  postTitle: string;
  postContent: string;
  postUrl?: string;
  subreddit?: string;
  initialKeywords?: string[];
  commentTarget?: CommentTarget;
  guidelines: SubredditGuidelinesResponse | null;
  /** Controlled from outside (provider-owned generation state) */
  loading: boolean;
  currentStage: number;
  onCanGenerateChange: (can: boolean) => void;
  /** Called when the user submits the form; caller owns the async API call */
  onStartGeneration: (params: GenerateParams) => void;
}

type ToneOption = {
  value: string;
  label: string;
  description: string;
  dot: string;
};

const toneOptions: ToneOption[] = [
  { value: 'neutral', label: 'Neutral', description: 'Balanced and objective while remaining human', dot: '#6B7280' },
  { value: 'casual', label: 'Casual', description: 'Laid-back, conversational, like texting a friend', dot: '#F59E0B' },
  { value: 'professional', label: 'Professional', description: 'Knowledgeable and articulate without being stuffy', dot: '#0079D3' },
  { value: 'humorous', label: 'Humorous', description: 'Witty and funny with natural, non-forced humor', dot: '#EAB308' },
  { value: 'empathetic', label: 'Empathetic', description: 'Warm, understanding, genuine emotional support', dot: '#06B6D4' },
  { value: 'enthusiastic', label: 'Enthusiastic', description: 'Energetic and passionate with authentic excitement', dot: '#FF4500' },
  { value: 'skeptical', label: 'Skeptical', description: 'Critical thinking, questions things respectfully', dot: '#7C3AED' },
  { value: 'supportive', label: 'Supportive', description: 'Encouraging and uplifting genuinely', dot: '#10B981' },
];

const ToneDot = ({ color }: { color: string }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: color,
      flexShrink: 0,
    }}
  />
);

const FieldLabel = ({
  children,
  hint,
}: {
  children: React.ReactNode;
  hint?: React.ReactNode;
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
    <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{children}</span>
    {hint && (
      <span style={{ fontSize: 12, fontWeight: 400, color: T.textMuted }}>{hint}</span>
    )}
  </span>
);

const toCommentContext = (comment: NestedComment): CommentContext => ({
  id: comment.id,
  body: comment.body,
  author: comment.author,
  score: comment.score,
  depth: comment.depth,
  is_submitter: comment.is_submitter,
});

const QuickReplyForm = forwardRef<QuickReplyFormHandle, QuickReplyFormProps>(
  (
    {
      postTitle,
      postContent,
      postUrl,
      subreddit,
      initialKeywords,
      commentTarget,
      guidelines,
      loading,
      currentStage,
      onCanGenerateChange,
      onStartGeneration,
    },
    ref
  ) => {
    const [form] = Form.useForm();
    const [keywords, setKeywords] = useState<string[]>([]);

    useEffect(() => {
      if (initialKeywords && initialKeywords.length > 0) {
        setKeywords(initialKeywords);
      }
    }, [initialKeywords]);

    useEffect(() => {
      onCanGenerateChange(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGenerate = async () => {
      const values = await form.validateFields();

      const capturedCommentId = commentTarget?.comment.id;

      const params: GenerateParams = {
        source: 'quick',
        post_title: postTitle,
        post_content: postContent,
        post_url: postUrl,
        subreddit,
        brand_info: values.brand_info,
        custom_wording: keywords.length > 0 ? keywords.join(', ') : undefined,
        additional_context: values.additional_context,
        tone_preference: values.tone_preference || 'neutral',
        force_brand_mention: !!values.force_brand_mention,
        target_comment: commentTarget
          ? toCommentContext(commentTarget.comment)
          : undefined,
        parent_comments: commentTarget
          ? commentTarget.parentChain.map(toCommentContext)
          : undefined,
        capturedCommentId,
      };

      onStartGeneration(params);
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        void handleGenerate();
      },
    }));

    return (
      <Form
        form={form}
        layout="vertical"
        initialValues={{ tone_preference: 'neutral' }}
        requiredMark={false}
      >
        <Form.Item
          label={<FieldLabel>Brand or product to embed</FieldLabel>}
          name="brand_info"
          tooltip="Enter your brand name, product, or service you want to naturally mention in the reply"
        >
          <Input
            placeholder="e.g., MyApp, TechSolutions"
            disabled={loading}
            style={{ borderRadius: 6 }}
          />
        </Form.Item>

        {guidelines?.restricts_promotion && (
          <Form.Item
            label={
              <FieldLabel
                hint={
                  <span style={{ color: T.warning }}>
                    r/{subreddit} restricts self-promotion. Forcing the brand may cause your reply to be removed.
                  </span>
                }
              >
                Force brand mention
              </FieldLabel>
            }
            name="force_brand_mention"
            valuePropName="checked"
            initialValue={false}
            tooltip={`r/${subreddit} restricts self-promotion. By default we omit the brand to comply. Turn this on to force the mention anyway.`}
            style={{ marginBottom: 16 }}
          >
            <Switch disabled={loading} size="small" />
          </Form.Item>
        )}

        <Form.Item
          label={
            <FieldLabel hint="Press Enter or comma to add">
              Keywords to include
            </FieldLabel>
          }
          tooltip="Keywords from your scraping settings. Add or remove as needed."
        >
          <KeywordsInput
            value={keywords}
            onChange={setKeywords}
            disabled={loading}
            tagColor="default"
          />
        </Form.Item>

        <Form.Item
          label={<FieldLabel>Tone preference</FieldLabel>}
          name="tone_preference"
          tooltip="Sets how the AI will communicate"
        >
          <Select
            placeholder="Select tone"
            disabled={loading}
            optionLabelProp="label"
            style={{ borderRadius: 6 }}
          >
            {toneOptions.map((opt) => (
              <Option
                key={opt.value}
                value={opt.value}
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <ToneDot color={opt.dot} />
                    {opt.label}
                  </span>
                }
              >
                <div style={{ padding: '4px 0' }}>
                  <div
                    style={{
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      color: T.text,
                    }}
                  >
                    <ToneDot color={opt.dot} />
                    {opt.label}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, marginLeft: 16 }}>
                    {opt.description}
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={<FieldLabel hint="Optional">Additional context</FieldLabel>}
          name="additional_context"
          tooltip="Any extra information you want the AI to consider"
        >
          <TextArea
            rows={3}
            placeholder="e.g., Focus on cost-effectiveness, mention our free trial"
            disabled={loading}
            style={{ borderRadius: 6 }}
          />
        </Form.Item>

        {loading && (
          <div
            style={{
              marginTop: 4,
              padding: '14px 14px 12px',
              backgroundColor: T.bgMuted,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Steps
                current={currentStage}
                size="small"
                items={[
                  {
                    title: 'Planning',
                    icon: currentStage === 0 ? <LoadingOutlined /> : undefined,
                  },
                  {
                    title: 'Drafting',
                    icon: currentStage === 1 ? <LoadingOutlined /> : undefined,
                  },
                  {
                    title: 'Humanizing',
                    icon: currentStage === 2 ? <LoadingOutlined /> : undefined,
                  },
                  {
                    title: 'Complete',
                    icon: currentStage === 3 ? <LoadingOutlined /> : undefined,
                  },
                ]}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <LoadingOutlined style={{ color: T.brand, fontSize: 12 }} />
                <Text style={{ color: T.textSecondary, fontSize: 13 }}>
                  Generating your reply…
                </Text>
              </div>
            </Space>
          </div>
        )}
      </Form>
    );
  }
);

QuickReplyForm.displayName = 'QuickReplyForm';
export default QuickReplyForm;
